"use client";

import React from "react";
import { AlertCircle, RefreshCw } from "lucide-react";

const DEV_ERROR_QUERY = "__forceAppErrorBoundary";

function AppErrorBoundaryDevProbe() {
  if (process.env.NODE_ENV === "production") return null;
  if (globalThis.window == "undefined") return null;
  const params = new URLSearchParams(globalThis.location.search);
  if (params.get(DEV_ERROR_QUERY) === "1") {
    throw new Error(
      `Dev-only: AppErrorBoundary test. Remove ?${DEV_ERROR_QUERY}=1 from the URL.`,
    );
  }
  return null;
}
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.props.onError?.(error, errorInfo);
    if (process.env.NODE_ENV !== "production") {
      console.error("[AppErrorBoundary]", error, errorInfo);
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.state.hasError) return;
    const { resetKey } = this.props;
    if (resetKey !== undefined && resetKey !== prevProps.resetKey) {
      this.setState({ hasError: false });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false });
  };

  render() {
    const {
      children,
      title = "Something went wrong",
      description = "This part of the screen hit an unexpected error. You can try again.",
      variant = "default",
      className = "",
    } = this.props;

    if (!this.state.hasError) {
      return (
        <>
          <AppErrorBoundaryDevProbe />
          {children}
        </>
      );
    }

    const isEmbedded = variant === "embedded";
    const containerClass = isEmbedded
      ? "flex flex-col items-center justify-center min-h-[280px] w-full py-8 px-4"
      : "flex flex-col items-center justify-center min-h-[400px] flex-1 w-full py-12 px-4";

    return (
      <div
        className={`${containerClass} ${className}`.trim()}
        role="alert"
        aria-live="assertive"
      >
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#1B5D97] text-white">
            <AlertCircle className="h-7 w-7" strokeWidth={2} aria-hidden />
          </div>
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <p className="mt-2 text-sm text-slate-600">{description}</p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg border-2 border-[#1D5D9B] bg-white px-4 py-2 text-sm font-medium text-[#1D5D9B] transition-colors hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-[#1D5D9B] focus:ring-offset-2"
          >
            <RefreshCw className="h-4 w-4 shrink-0" aria-hidden />
            Retry
          </button>
        </div>
      </div>
    );
  }
}
